import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpStatusCode } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { API_ENDPOINT, AUTH_TOKEN_INTERCEPTOR_FILTER, CONFIG_LOG, DEVICE_LOGIN_HEADERS, GLOBAL_DATA_KEY, LOCAL_STORAGE_KEY, SAUCER_LOG_TOKEN, SESSION_STORAGE_KEY } from '@core/configs';
import { AuthService } from '@core/services/apis';
import { GlobalDataService, HttpCancelService, SessionContextService, SessionStorageService } from '@core/services/common';
import { BehaviorSubject, Observable, Subject, throwError } from 'rxjs';
import { catchError, filter, switchMap, take, takeUntil, tap } from 'rxjs/operators';

export interface UserSessionInfo {
  accessToken: string;
  stfCd: string;
  companyId: string;
  corpPrefix: string;
  officeCd: string;
  officeVersion: string;
  corpCd: string;
}

@Injectable()
export class JWTInterceptor implements HttpInterceptor {
  private refreshTokenInProgress = false;
  private refreshTokenSubject: Subject<string> = new BehaviorSubject<any>(null);

  constructor(
    private authService: AuthService,
    private cancelService: HttpCancelService,
    private sessionStorageService: SessionStorageService,
    private globalDataService: GlobalDataService,
    private sessionContextService: SessionContextService,
    @Inject(AUTH_TOKEN_INTERCEPTOR_FILTER)
    protected filterRequest: (req: HttpRequest<any>) => boolean,
  ) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const isDeviceLoginRequest = request.headers.has(DEVICE_LOGIN_HEADERS.token);
    const userSession = this.sessionStorageService.getUserSession();
    const stfCd = this.sessionContextService.getStaffCd();
    const officeSelected = this.sessionContextService.getOfficeSelected<any>();
    const companyId = officeSelected?.companyId;
    const corpCd = officeSelected?.corpCd;
    let corpPrefix = "";
    let officeCd = "";
    let officeVersion = "";
    try {
        corpPrefix = this.globalDataService.get(GLOBAL_DATA_KEY.corpPrefix) || localStorage.getItem(LOCAL_STORAGE_KEY.corpPrefix) || "";
        officeCd = this.globalDataService.get(GLOBAL_DATA_KEY.officeCd) || officeSelected?.officeCd || "";
        officeVersion = this.globalDataService.get(GLOBAL_DATA_KEY.officeVersion) || this.sessionStorageService.getSessionData(SESSION_STORAGE_KEY.FORCE_LOGOUT_VERSION) || "";
    } catch {}

    const sessionInfo: UserSessionInfo = {
      accessToken: userSession?.accessToken ?? "",
      stfCd: stfCd ?? "",
      companyId: companyId ?? "",
      corpPrefix: corpPrefix ?? "",
      officeCd: officeCd ?? "",
      officeVersion: officeVersion ?? "",
      corpCd: corpCd ?? ""
    };
    
    if (
      request.url.includes(API_ENDPOINT.saucerLog.logBulk) ||
      request.url === CONFIG_LOG.API_GET_CLIENT_IP
    ) {
      return next.handle(this.injectToken(request, sessionInfo)).pipe(
        takeUntil(
          this.cancelService.cancelPendingRequest$.pipe(
            tap(() => {
              throw new Error('Request is canceled');
            }),
          ),
        ),
      );
    }
    
    if (!this.filterRequest(request) && !isDeviceLoginRequest && !(request.body instanceof File) && userSession && userSession.accessToken) {
      if (
        request.url.includes(API_ENDPOINT.token.refreshToken) || 
        request.url.includes(API_ENDPOINT.saucerLog.logBulk) ||
        request.url === CONFIG_LOG.API_GET_CLIENT_IP
      )
      {
        return next.handle(this.injectToken(request, sessionInfo)).pipe(
          takeUntil(
            this.cancelService.cancelPendingRequest$.pipe(
              tap(() => {
                throw new Error('Request is canceled');
              }),
            ),
          ),
        );
      }
      return next.handle(this.injectToken(request, sessionInfo)).pipe(
        takeUntil(
          this.cancelService.cancelPendingRequest$.pipe(
            tap(() => {
              throw new Error('Request is canceled');
            }),
          ),
        ),
        catchError((error) => {
          if (error.status === HttpStatusCode.Unauthorized) {
            if (!this.refreshTokenInProgress) {
              this.refreshTokenInProgress = true;
              this.refreshTokenSubject.next("");
              return this.authService.requestAccessToken(userSession.refreshToken).pipe(
                switchMap((res) => {
                  if (!res) {
                    this.authService.logout();
                  }
                  this.refreshTokenInProgress = false;
                  void this.authService.applyRefreshedSession(res.result);
                  this.refreshTokenSubject.next(res.result.accessToken);

                  const refreshedSessionInfo: UserSessionInfo = {
                    accessToken: res.result.accessToken ?? "",
                    stfCd: stfCd ?? "",
                    companyId: companyId ?? "",
                    corpPrefix: corpPrefix ?? "",
                    officeCd: officeCd ?? "",
                    officeVersion: officeVersion ?? "",
                    corpCd: corpCd ?? ""
                  };

                  return next.handle(this.injectToken(request, refreshedSessionInfo)).pipe(
                    takeUntil(
                      this.cancelService.cancelPendingRequest$.pipe(
                        tap(() => {
                          throw new Error('Request is canceled');
                        }),
                      ),
                    ),
                  );
                }),
                catchError((e) => {
                  this.refreshTokenInProgress = false;
                  if (e.status === HttpStatusCode.Unauthorized) {
                    this.authService.logout();
                  }
                  return throwError(error);
                }),
              );
            } else {
              return this.refreshTokenSubject.pipe(
                filter(result => result !== ""),
                take(1),
                switchMap((at) => {
                  const refreshedSessionInfo: UserSessionInfo = {
                    accessToken: at ?? "",
                    stfCd: stfCd ?? "",
                    companyId: companyId ?? "",
                    corpPrefix: corpPrefix ?? "",
                    officeCd: officeCd ?? "",
                    officeVersion: officeVersion ?? "",
                    corpCd: corpCd ?? ""
                  };
                  return next.handle(this.injectToken(request, refreshedSessionInfo)).pipe(
                    takeUntil(
                      this.cancelService.cancelPendingRequest$.pipe(
                        tap(() => {
                          throw new Error('Request is canceled');
                        }),
                      ),
                    ),
                  );
                }),
              );
            }
          }
          return throwError(error);
        }),
      );
    } else {
      return next.handle(request);
    }
  }

  injectToken(request: HttpRequest<any>, sessionInfo: UserSessionInfo): HttpRequest<any> {
    let token = `Bearer ${sessionInfo.accessToken}`;
    if (request.url.includes(API_ENDPOINT.saucerLog.logBulk)) {
      token = `Bearer ${SAUCER_LOG_TOKEN}`;
    } 
    else if (request.url === CONFIG_LOG.API_GET_CLIENT_IP){
      token = '';
    }
    return request.clone({
      setHeaders: {
        authorization: token,
        stfCd: `${sessionInfo.stfCd}`,
        corpCd: `${sessionInfo.corpCd}`,
        companyId: `${sessionInfo.companyId}`,
        corpPrefix: `${sessionInfo.corpPrefix}`,
        officeCd: `${sessionInfo.officeCd}`,
        officeVersion: `${sessionInfo.officeVersion}`
      },
    });
  }
}